import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import type { ReelsPlanPostsPerDay } from '../../../../../../redux/api/fymAPI.types';
import { SMLUpgradeFeature } from '../../../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../../../hooks/useUpgradePrompt';
import { reelsPlanMessages } from './reelsPlanMessages';
import { reelsPlanPostsOptions } from './reelsPlanModel';
import { ReelsPlanSegment } from './ReelsPlanSegment';

interface ReelsPlanPostsPickerProps {
  postsPerDay: ReelsPlanPostsPerDay;
  /** The most posts a day of the workspace plan (1 on Free and the trial). */
  maxPostsPerDay: number;
  disabled: boolean;
  onChange: (postsPerDay: ReelsPlanPostsPerDay) => void;
}

/**
 * "Posts a day" of the setup (owner decision 2026-10-19): 1, 2 or 3. Each post is its own AI reel.
 * An option above the plan shows a lock, and its click opens the generic plan offer.
 */
const ReelsPlanPostsPickerComponent: React.FC<ReelsPlanPostsPickerProps> = ({
  postsPerDay,
  maxPostsPerDay,
  disabled,
  onChange,
}) => {
  const intl = useIntl();
  const openUpgrade = useUpgradePrompt();
  const choices = useMemo(() => reelsPlanPostsOptions(maxPostsPerDay), [maxPostsPerDay]);
  const options = useMemo(
    () =>
      choices.map((choice) => ({
        value: String(choice.value),
        label: intl.formatMessage(reelsPlanMessages.postsPerDayOption, { count: choice.value }),
        locked: choice.locked,
      })),
    [choices, intl]
  );
  const pick = useCallback(
    (value: string) => {
      const next = choices.find((choice) => String(choice.value) === value);
      if (next !== undefined && !next.locked) {
        onChange(next.value);
      }
    },
    [choices, onChange]
  );
  const offerPlan = useCallback(() => openUpgrade(SMLUpgradeFeature.generic), [openUpgrade]);
  return (
    <ReelsPlanSegment
      label={intl.formatMessage(reelsPlanMessages.postsPerDay)}
      options={options}
      value={String(postsPerDay)}
      disabled={disabled}
      onChange={pick}
      onLocked={offerPlan}
    />
  );
};

export const ReelsPlanPostsPicker = TypedMemo(ReelsPlanPostsPickerComponent);
